import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
} from "@mui/material";
import { ReactElement } from "react";
import * as Yup from "yup";

import { shortCodeConfigSchema } from "@stw/yup-validators";

import {
  EditOwnedPhoneNumberInput,
  PhoneNumberMessageClass,
} from "~frontend/__generated__/graphql";
import { FormikSelect } from "~frontend/components/forms";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import PhoneNumberConfig, {
  shortCodeConfigToFormValues,
  shortCodeFormValuesToConfig,
} from "./PhoneNumberConfig";
import { OwnedPhoneNumberForList } from "./types";

export default function EditOwnedPhoneNumberDialog({
  open,
  ownedPhoneNumber,
  onClose,
  onSubmit,
}: {
  open: boolean;
  ownedPhoneNumber: OwnedPhoneNumberForList | null;
  onClose: () => void;
  onSubmit: (values: EditOwnedPhoneNumberInput) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();

  const isStaff = useCurrentUserIsStaff();

  return (
    <FormikDialog
      initialValues={{
        [submitErrorField]: null,
        displayName: ownedPhoneNumber?.displayName ?? "",
        tcrCampaignId: ownedPhoneNumber?.tenDlcCampaignId ?? "",
        shortCodeConfig: shortCodeConfigToFormValues(
          ownedPhoneNumber?.shortCodeConfig ?? null
        ),
        state: isStaff ? (ownedPhoneNumber?.state ?? "NEW") : undefined,
      }}
      validationSchema={Yup.object().shape({
        displayName: Yup.string(),
        tcrCampaignId: isStaff
          ? Yup.string()
              .test(
                "10dlc-campaign-id-presence",
                "Cannot delete TCR Campaign ID",
                value => {
                  // Do not allow staff to delete the TCR Campaign ID if one is already set
                  if (
                    ownedPhoneNumber?.messageClass ===
                      PhoneNumberMessageClass.TenDlc &&
                    ownedPhoneNumber?.tenDlcCampaignId
                  ) {
                    return !!value;
                  }

                  return true;
                }
              )
              .test(
                "10dlc-campaign-id-format",
                "Invalid TCR Campaign ID",
                value => {
                  if (value) {
                    return /^(C|c).{6}$/.test(value);
                  }

                  return true;
                }
              )
          : Yup.mixed(),
        shortCodeConfig:
          ownedPhoneNumber?.messageClass === PhoneNumberMessageClass.ShortCode
            ? shortCodeConfigSchema(Yup, {
                useCommaSeparatedLists: true,
              })
            : Yup.mixed(),
        state: isStaff ? Yup.string().oneOf(["NEW", "ACTIVE"]) : Yup.mixed(),
      })}
      onSubmit={async (values, formik) => {
        if (!ownedPhoneNumber) {
          throw new Error("ownedPhoneNumber must be set if the dialog is open");
        }
        try {
          await onSubmit({
            displayName: values.displayName || null,
            ownedPhoneNumberId: ownedPhoneNumber.id,
            tcrCampaignId: values.tcrCampaignId || null,
            shortCodeConfig: shortCodeFormValuesToConfig(
              ownedPhoneNumber.messageClass,
              values.shortCodeConfig
            ),
            state:
              isStaff && ownedPhoneNumber.messageClass === "SHORT_CODE"
                ? values.state
                : undefined,
          });
          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
      open={open}
      maxWidth="md"
      fullWidth
    >
      {({ isSubmitting, isValid }) => (
        <>
          <DialogTitle>Edit Phone Number</DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <FormikErrorBanner />
            <FormikTextField
              name="displayName"
              label="Display Name"
              fullWidth
            />

            <PhoneNumberConfig
              messageClass={ownedPhoneNumber?.messageClass ?? null}
            />

            {isStaff &&
              ownedPhoneNumber?.messageClass ===
                PhoneNumberMessageClass.TenDlc && (
                <FormikTextField
                  name="tcrCampaignId"
                  label="TCR Campaign ID"
                  fullWidth
                />
              )}

            {isStaff &&
              ownedPhoneNumber?.messageClass ===
                PhoneNumberMessageClass.ShortCode && (
                <FormikSelect name="state" label="Status" fullWidth>
                  <MenuItem value="NEW">
                    Pending (not usable by clients, can be tested by carriers)
                  </MenuItem>
                  <MenuItem value="ACTIVE">
                    Active (approved by carriers and ready for client use)
                  </MenuItem>
                </FormikSelect>
              )}
          </DialogContent>

          <DialogActions>
            <Button disabled={isSubmitting} onClick={onClose} color="secondary">
              Cancel
            </Button>

            <FormikSubmitButton
              disabled={!isValid || isSubmitting}
              color="primary"
              variant="contained"
              submitText="Update"
              loadingText="Updating..."
              data-testid="edit-owned-phone-number-display-name-button"
            />
          </DialogActions>
        </>
      )}
    </FormikDialog>
  );
}
